import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { AddSystemChargeWrapper } from "./styled";
import { AppContext } from "../../../../context/appContext";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { useNavigate } from "react-router-dom";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { createSystemChargeService } from "../../../../utils/api/configuration/systemcharge/createSystemCharge";
import Cookies from "universal-cookie";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { retrieveAllSavingsProductsService } from "../../../../utils/api/configuration/savingsproduct/retrieveAllSavingsProducts";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { BaseButton } from "../../../../components/button/styled";
import { retrieveAllLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveAllLoanProduct";

export const AddSystemCharge = () => {
	const modes = ["auto", "manual"];
	const actions = ["trigger", "schedule"];
	const options = ["yes", "no"];
	const types = [
		"deposit",
		"withdrawal",
		"disbursement",
		"FTD termination",
		"SMS alert",
		"mandatory",
	];
	const productTypes = ["savings", "loan"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [formDetails, setFormDetails] = useState({
		name: "",
		mode: " ",
		glAccount: " ",
		type: " ",
		linkedProductId: " ",
		linkedProductType: " ",
		amount: "",
		action: " ",
		allowOverride: " ",
	});

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: savingsProducts } = useQuery({
		queryKey: ["savingsProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllSavingsProductsService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const { data: loanProducts } = useQuery({
		queryKey: ["loanProducts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllLoanProductService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const { data: glAccounts } = useQuery({
		queryKey: ["glAccounts", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return (
				response?.data?.filter(
					(gl: Record<string, any>) => gl.glAccountType === "GL Account",
				) ?? []
			);
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
			...(name === "linkedProductType" && { linkedProductId: " " }),
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			const response = await createSystemChargeService(TOKEN, formDetails);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to create system charge. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to create system charge. ${error.message}`);
			console.error("Failed to create system charge:", error);
		}
	};

	return (
		<AppLayout pageId={"System-Charge"} pageTitle={"Add System Charge"}>
			<AddSystemChargeWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="System Charge Added Successfully"
					message="Your information has been successfully added to the system."
					callToAction="Back to System Charge List"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to add system charge
					</Typography>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Charge Name</BaseLabel>
								<BaseInput
									required
									name="name"
									value={formDetails.name}
									placeholder="Enter Charge Name"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Charge Mode</BaseLabel>
								<BaseSelect
									name="mode"
									fontweight={400}
									value={formDetails.mode}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Mode</BaseOption>
									{modes.map((mode: string, index: number) => (
										<BaseOption key={index} value={mode}>
											{mode.charAt(0).toUpperCase() + mode.slice(1)}
										</BaseOption>
									))}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Charge Type</BaseLabel>
								<BaseSelect
									name="type"
									fontweight={400}
									value={formDetails.type}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Type</BaseOption>
									{types.map((type: string, index: number) => (
										<BaseOption key={index} value={type}>
											{type.charAt(0).toUpperCase() + type.slice(1)}
										</BaseOption>
									))}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Product Type</BaseLabel>
								<BaseSelect
									name="linkedProductType"
									fontweight={400}
									value={formDetails.linkedProductType}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Product</BaseOption>
									{productTypes?.map((type: string, index: number) => (
										<BaseOption key={index} value={type}>
											{type
												?.split(" ")
												.map(
													(word: string) =>
														word.charAt(0).toUpperCase() + word.slice(1),
												)
												.join(" ")}
										</BaseOption>
									))}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						{formDetails.linkedProductType.trim() && (
							<Grid2 size={{ mobile: 12, tablet: 6 }}>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">Product</BaseLabel>
									<BaseSelect
										name="linkedProductId"
										fontweight={400}
										value={formDetails.linkedProductId}
										colour="var(--semi-gray-color)"
										onChange={(e) => handleChange(e)}
									>
										<BaseOption value=" ">Select Product</BaseOption>
										{formDetails.linkedProductType === "savings" &&
											savingsProducts?.map(
												(product: Record<string, any>, index: number) => (
													<BaseOption key={index} value={product.id}>
														{product.name}
													</BaseOption>
												),
											)}
										{formDetails.linkedProductType === "loan" &&
											loanProducts?.map(
												(product: Record<string, any>, index: number) => (
													<BaseOption key={index} value={product.id}>
														{product.name}
													</BaseOption>
												),
											)}
									</BaseSelect>
								</BaseFieldSet>
							</Grid2>
						)}
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">GL Account</BaseLabel>
								<BaseSelect
									name="glAccount"
									fontweight={400}
									value={formDetails.glAccount}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select GL Account</BaseOption>
									{glAccounts?.map(
										(glAccount: Record<string, any>, index: number) => (
											<BaseOption key={index} value={glAccount.id}>
												{glAccount.glName
													?.split(" ")
													.map(
														(word: string) =>
															word.charAt(0).toUpperCase() + word.slice(1),
													)
													.join(" ")}
											</BaseOption>
										),
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Amount</BaseLabel>
								<BaseInput
									required
									name="amount"
									value={formDetails.amount}
									placeholder="Enter Amount"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Allow Override</BaseLabel>
								<BaseSelect
									name="allowOverride"
									fontweight={400}
									value={formDetails.allowOverride}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Option</BaseOption>
									{options?.map((option: string, index: number) => (
										<BaseOption key={index} value={option}>
											{option.charAt(0).toUpperCase() + option.slice(1)}
										</BaseOption>
									))}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Action Required
								</BaseLabel>
								<BaseSelect
									name="action"
									fontweight={400}
									value={formDetails.action}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value=" ">Select Action</BaseOption>
									{actions?.map((action: string, index: number) => (
										<BaseOption key={index} value={action}>
											{action
												?.split(" ")
												.map(
													(word: string) =>
														word.charAt(0).toUpperCase() + word.slice(1),
												)
												.join(" ")}
										</BaseOption>
									))}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Submit
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</AddSystemChargeWrapper>
		</AppLayout>
	);
};
